import clsx from 'clsx';
import { CSSProperties, MouseEvent } from 'react';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';

import { ClientImageSourceSet, CropHint } from '@vestico/model';

import { useImageBorderStyles } from '../hooks';
import { BorderRadiusStyles } from '../model/styles';
import { ImageDisplay } from './ImageDisplay';
import style from './MediaDisplay.module.css';
import { MediaObjectFit } from './MediaDisplayConstants';
import { VideoDisplay } from './VideoDisplay';
import { VideoPlayIcon } from './VideoPlayIcon';

export interface MediaDisplayProps {
  imageURL?: string;
  srcSet?: ClientImageSourceSet;
  thumbURL?: string;
  videoURL?: string;
  mimeType: string;
  playVideo?: boolean;
  alwaysShowVideo?: boolean;
  hideVideoIcon?: boolean;
  disableVideoControls?: boolean;
  cropHint?: CropHint;
  borderRadius?: BorderRadiusStyles;
  mediaFit?: MediaObjectFit;
  customCSS?: CSSProperties;
  loadInView?: boolean;
  animate?: boolean;
  onLoad?: () => void;
  onClick?: (e: MouseEvent<HTMLImageElement | HTMLVideoElement>) => void;
  onMouseDown?: (e: MouseEvent<HTMLImageElement | HTMLVideoElement>) => void;
}

export const MediaDisplay = ({
  imageURL,
  videoURL,
  thumbURL,
  srcSet,
  mimeType,
  playVideo,
  alwaysShowVideo,
  onLoad,
  onClick,
  onMouseDown,
  borderRadius,
  loadInView,
  animate,
  mediaFit,
  customCSS,
  hideVideoIcon,
  cropHint,
  disableVideoControls,
}: MediaDisplayProps) => {
  const [didLoad, setDidLoad] = useState(false);
  const [didLoadVideo, setDidLoadVideo] = useState(false);

  useLayoutEffect(() => {
    setDidLoad(false);
    setDidLoadVideo(false);
  }, [imageURL, videoURL, thumbURL]);

  useLayoutEffect(() => {
    if (!playVideo) {
      setDidLoadVideo(false);
    }
  }, [playVideo]);

  const onLoadCallback = useCallback(() => {
    if (onLoad) {
      onLoad();
    }
    setDidLoad(true);
  }, [onLoad]);

  const onVideoLoad = useCallback(() => {
    setDidLoadVideo(true);
    onLoadCallback();
  }, [onLoadCallback]);

  const borderStyles = useImageBorderStyles(borderRadius, '--media-radius');

  const innerBorderStyles = useImageBorderStyles(['0']);
  const cssStyle = useMemo(() => {
    let css = { ...innerBorderStyles, ...borderStyles };
    if (customCSS) {
      css = { ...css, ...customCSS };
    }

    return css;
  }, [innerBorderStyles, borderStyles, customCSS]);

  const videoMarkup =
    videoURL && playVideo ? (
      <div className={clsx(style.video, { [style.visible]: didLoadVideo || alwaysShowVideo })}>
        <VideoDisplay
          url={videoURL}
          coverURL={thumbURL}
          coverSrcSet={srcSet}
          mimeType={mimeType}
          onLoad={onVideoLoad}
          onClick={onClick}
          onMouseDown={onMouseDown}
          loadInView={loadInView}
          videoFit={mediaFit}
          cropHint={cropHint}
          disableControls={disableVideoControls}
        />
      </div>
    ) : null;

  if (imageURL || videoURL) {
    return (
      <div className={clsx(style.root)} style={cssStyle}>
        {videoMarkup}
        {imageURL ? (
          <>
            <ImageDisplay
              url={imageURL}
              srcSet={srcSet}
              thumbURL={thumbURL}
              onLoad={onLoadCallback}
              onClick={onClick}
              onMouseDown={onMouseDown}
              loadInView={loadInView}
              animate={animate}
              imageFit={mediaFit}
              cropHint={cropHint}
            />
            {didLoad && !hideVideoIcon ? <VideoPlayIcon mimeType={mimeType} /> : null}
          </>
        ) : null}
      </div>
    );
  }
  return null;
};
